/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, * as Msdyn365 from '@msdyn365-commerce/core';
import axios from 'axios';
import { 
    generateEmailContactPayload, 
    getEmarsysHeader 
} from './utilities/emarsys-utils';

// tslint:disable-next-line
const EMARSYS_API_BASE_URL = 'https://api.emarsys.net';

/**
 * PostInStoreGiveawayEvent Input Action
 */
export class PostInStoreGiveawayEventInput implements Msdyn365.IActionInput {
    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `PostInStoreGiveawayEventCacheKey`;
    public getCacheObjectType = () => 'PostInStoreGiveawayEventObjectType';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

export interface IPostInStoreGiveawayEventData {
    Result: string;
    Message: string;
    ErrorMessage: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostInStoreGiveawayEventInput();
};

function generateErrorResponse(errorMessageString: string) {
    const errorResponse = 'Sorry, there was an issues while entering the competition, please try again later or contact support';
    return {
        Result: 'Error',
        Message: errorResponse,
        ErrorMessage: errorMessageString
    };
}

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: PostInStoreGiveawayEventInput, ctx: Msdyn365.IActionContext): Promise<IPostInStoreGiveawayEventData> {
    const postBody = ctx.requestContext.postBody;
    const contentObject = postBody?.content;
    const bodyObject = JSON.parse(contentObject!.toString());

    if (ctx.requestContext.query?.failServerSide === 'true') {
        if (!msdyn365Commerce.isBrowser) {
            throw new Error('Thrown action');
        } else {
            return generateErrorResponse('Post in-store giveaway: server side fail 1');
        }
    } else if (ctx.requestContext.query?.returnMockData === 'true') {
        return generateErrorResponse('Post in-store giveaway: server side fail 2');
    }

   try {
        const usernameKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPIUsernameAU' : 'EmarsysAPIUsernameNZv2';
        const secretKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPISecretAU' : 'EmarsysAPISecretNZv2';
        const user = await msdyn365Commerce.secretManager?.getSecretValue(usernameKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        const secret = await msdyn365Commerce.secretManager?.getSecretValue(secretKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        if (!user?.value || !secret?.value) {
            const errors = {
                usernameError: user?.error?.message,
                secretError: user?.error?.message
            };
            return generateErrorResponse(`Post in-store giveaway: unable to get keyvault values ${JSON.stringify(errors)}`);
        }

        const headers = getEmarsysHeader(user?.value || '', secret?.value || '');
        let emarsysPostBody = generateEmailContactPayload(bodyObject?.emailAddress, bodyObject?.firstName, bodyObject?.lastName, bodyObject?.phoneNumber);
        if (bodyObject?.optIn) {
            emarsysPostBody[bodyObject.emarsysGroupId] = '1';
            emarsysPostBody['31'] = '1'
        }
        emarsysPostBody[bodyObject?.emarsysInStoreGiveawayGroupId] = '1';
        emarsysPostBody[bodyObject?.emarsysGroupIdAmountSpent] = bodyObject?.amountSpent;

        return await axios.put(`${EMARSYS_API_BASE_URL}/api/v2/contact/?create_if_not_exists=1`,
            emarsysPostBody,
            {
                headers: headers
            })
            .then((response) => {
                return {
                    Result: 'Success',
                    Message: '',
                    ErrorMessage: `Success message: ${JSON.stringify(response.data)}`
                };                
            })
            .catch((error) => {
                const errToReturn = error && error.response ? error.response.data : error
                return generateErrorResponse(`Post in-store giveaway: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
            });
    } catch (e) {
        return generateErrorResponse(`Post in-store giveaway: catch ${JSON.stringify(e)}`);
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostInStoreGiveawayEventData>>action,
    id: 'PostInStoreGiveawayEvent',
    input: createInput
});
